import { useEffect, useState } from 'react';
import { Communicator } from '../../tools/Communication';
import { ResponsiveTable } from '../../components/Layout';
import i18n, { getDateFormatOptions } from '@/utils/i18n';
import { FaTrashAlt } from 'react-icons/fa';
import { LayoutStoreInstance } from '../../LayoutStore';
import { isNullOrWhitespace } from '@/utils/stringHelper';
import HelpPopover from '../../components/HelpPopover';
import { Button } from 'reactstrap';

type LinkedOfflineUsersProps = {
  vytalId: string;
};

interface LinkedOfflineUser {
  primaryUserId: string;
  secondaryUserId: string;
  signupCode: string;
  mergeDate: string;
}

export const LinkedOfflineUsersTab = function LinkedOfflineUsersTab({
  vytalId,
}: LinkedOfflineUsersProps) {
  const [linkedOfflineUsers, setLinkedOfflineUsers] = useState(
    [] as LinkedOfflineUser[],
  );

  useEffect(() => {
    realoadLinkedOfflineCards();
  }, []);

  function realoadLinkedOfflineCards() {
    Communicator.Get('/Users/GetLinkedOfflineCards', { userId: vytalId })
      .then((response) => response.json())
      .then((linkedUsers: LinkedOfflineUser[]) =>
        setLinkedOfflineUsers(linkedUsers),
      );
  }

  async function unlinkOfflineCard(offlineCard: LinkedOfflineUser) {
    const result = await Communicator.Get(
      'api/3/OfflineCard/GetUserStatusInfo',
      { code: `u${offlineCard.secondaryUserId}` },
    );
    if (result.status != 200) {
      LayoutStoreInstance.setError('Error unlinking offline cards');
    }
    realoadLinkedOfflineCards();
  }

  function getUnlinkOfflineCardElement(offlineUser: LinkedOfflineUser) {
    if (!isNullOrWhitespace(offlineUser.signupCode)) {
      return (
        <span className="ms-1">
          <Button
            size="l"
            type="button"
            onClick={() => unlinkOfflineCard(offlineUser)}
          >
            <FaTrashAlt />
          </Button>
        </span>
      );
    } else {
      return (
        <HelpPopover
          size="l"
          text="After unlinking an offline card a link will still persist for statistical reasons. This link does not affect the offline card anymore. You can still see if the unlinking was successful since the singup code is no longer present and the offline user id changed to a new random id. Furthermore you can now find the offline user again in the user list."
          id={`${offlineUser.secondaryUserId}-help`}
        />
      );
    }
  }

  return (
    <div>
      <h3>Linked Offline Cards</h3>
      <br />
      <p>
        After unlinking an offline card a link will still persist for
        statistical reasons. This link does not affect the offline card anymore.
        You can still see if the unlinking was successful since the singup code
        is no longer present and the offline user id changed to a new random id.
        Furthermore you can now find the offline user again in the user list.
      </p>
      <br />
      <ResponsiveTable>
        <thead>
          <tr>
            <th>Offline user id</th>
            <th>Offline user singup code</th>
            <th>Merge date</th>
            <th>Unlink offline card</th>
          </tr>
        </thead>
        <tbody>
          {linkedOfflineUsers.map((offlineUser) => (
            <tr key={offlineUser.secondaryUserId} style={{ cursor: 'pointer' }}>
              <td>{offlineUser.secondaryUserId}</td>
              <td>{offlineUser.signupCode}</td>
              <td>
                {new Date(offlineUser.mergeDate).toLocaleDateString(
                  i18n.language,
                  getDateFormatOptions(),
                )}
              </td>
              <td>{getUnlinkOfflineCardElement(offlineUser)}</td>
            </tr>
          ))}
        </tbody>
      </ResponsiveTable>
    </div>
  );
};
